/** THE DOCK ITSELF — mounted once, above the routes.
 *
 *  Renders the helper panel and, when it is collapsed, the thin bar on the
 *  right edge (a small bar at the bottom on a phone) with a small Sam, the
 *  speaker and the microphone. The panel component is never unmounted while
 *  docked, which is what keeps the conversation and a half-typed question
 *  alive across page changes.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mic, Volume2, VolumeX } from 'lucide-react';
import { useHelperDock } from '../../lib/HelperDockContext';
import { useAuth } from '../../lib/auth';
import * as sv from '../../lib/samVoice';
import HelperPanel from './HelperPanel';
import SamCharacter from './SamCharacter';

export const HelperDock = () => {
    const { t } = useTranslation();
    const dock = useHelperDock();
    const { user } = useAuth();
    const uid = user?.id || user?.email || 'anon';
    const [prefs, setPrefs] = useState(() => sv.readPrefs(uid));

    useEffect(() => {
        const sync = () => setPrefs(sv.readPrefs(uid));
        sync();
        window.addEventListener('sam-prefs', sync);
        return () => window.removeEventListener('sam-prefs', sync);
    }, [uid]);

    if (!dock.allowed) return null;

    const slim = dock.mode === 'slim';
    const iconBtn = 'w-9 h-9 rounded-xl grid place-items-center text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800';
    return (
        <>
            {/* THE THIN BAR. Right edge on a tablet or desktop, a small bar at
                the bottom on a phone. One tap on Sam brings the panel back. */}
            {slim && (
                <div data-testid="helper-slim-bar"
                     className={dock.isPhone
                         ? 'fixed inset-x-0 bottom-0 z-[59] h-12 border-t bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 flex items-center justify-center gap-3 shadow-[0_-2px_10px_rgba(0,0,0,0.08)]'
                         : 'fixed right-0 top-0 bottom-0 z-[59] w-12 border-l bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 flex flex-col items-center gap-3 py-3'}>
                    <button type="button" onClick={dock.restore}
                            data-testid="helper-slim-restore" className={iconBtn}
                            aria-label={t('helper.dock.restore')} title={t('helper.dock.restore')}>
                        <SamCharacter mood="calm" size="slim" name="Sam" className="w-8 h-8" />
                    </button>
                    {/* SPEAKER and MIC, always visible, in the slot kept for them. */}
                    <button type="button" data-testid="sam-speaker-slim" className={iconBtn}
                            aria-pressed={prefs.speaker}
                            onClick={() => {
                                const next = { ...prefs, speaker: !prefs.speaker };
                                sv.writePrefs(uid, next);
                                setPrefs(next);
                                if (!next.speaker) sv.stop();
                            }}
                            aria-label={prefs.speaker ? t('sam.speakerOff') : t('sam.speakerOn')}
                            title={prefs.speaker ? t('sam.speakerOff') : t('sam.speakerOn')}>
                        {prefs.speaker ? <Volume2 size={18} aria-hidden="true" />
                                       : <VolumeX size={18} aria-hidden="true" />}
                    </button>
                    <button type="button" data-testid="sam-mic-slim" className={iconBtn}
                            onClick={dock.askMic}
                            aria-label={t('sam.micStart')} title={t('sam.micStart')}>
                        <Mic size={18} aria-hidden="true" />
                    </button>
                </div>
            )}
            <HelperPanel open={dock.mode === 'overlay' || dock.mode === 'pinned'} />
        </>
    );
};

export default HelperDock;
